// See docs/architecture/checkout-overrides.md.

/** Checkout's text fields other than `submitButtonLabel`, each holding a translation key. */
export const CHECKOUT_TEXT_FIELDS = [
  'submitButtonProcessingLabel',
  'cardNumberLabel',
  'expiryDateLabel',
  'cvvLabel',
  'cardholderNameLabel',
  'cardNumberPlaceholder',
  'expiryDatePlaceholder',
  'cvvPlaceholder',
  'cardholderNamePlaceholder',
  'cardNumberErrorMessage',
  'expiryDateErrorMessage',
  'cvvErrorMessage',
  'cardholderNameErrorMessage',
  'requiredFieldErrorMessage',
] as const

export type CheckoutTextField = (typeof CHECKOUT_TEXT_FIELDS)[number]

/** Resolved, non-empty texts; a missing entry keeps the provider's own copy. */
export type CheckoutTexts = Partial<Record<CheckoutTextField, string>>

/** Yuno SDK i18n keys per text field; a field Yuno cannot show is absent. */
export const YUNO_TEXT_KEYS: Partial<Record<CheckoutTextField, readonly string[]>> = {
  cardNumberLabel: ['minimal_card_number_label', 'card_number_label'],
  expiryDateLabel: ['minimal_card_expiration_label', 'card_expiration_float_label'],
  cvvLabel: ['minimal_cvv_label', 'cvv_label_float_label'],
  cardholderNameLabel: ['minimal_card_holder_name_label', 'card_holder_name_float_label'],
  cardNumberPlaceholder: ['minimal_card_number_placeholder', 'card_number_placeholder'],
  expiryDatePlaceholder: ['minimal_card_expiration_placeholder', 'card_expiration_placeholder'],
  cvvPlaceholder: ['minimal_cvv_placeholder', 'cvv_placeholder'],
  cardNumberErrorMessage: ['card_number_invalid', 'credit_card_number_invalid'],
  expiryDateErrorMessage: [
    'card_expiration_invalid',
    'expiration_date_invalid',
    'expiration_month_invalid',
    'expiration_year_invalid',
    'expiration_date_expired',
    'expiration_date_incomplete',
  ],
  cvvErrorMessage: ['cvv_invalid', 'cvv_invalid_amex', 'cvv_invalid_all_cards'],
  cardholderNameErrorMessage: ['card_holder_name_invalid'],
  requiredFieldErrorMessage: ['field_is_required'],
}

const YUNO_HTML_TEXTS = new Set<CheckoutTextField>([
  'cardNumberErrorMessage',
  'expiryDateErrorMessage',
  'cvvErrorMessage',
  'cardholderNameErrorMessage',
  'requiredFieldErrorMessage',
])

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/**
 * @param texts the author's resolved texts
 * @returns Yuno's `texts` option: one entry per SDK key of every text that is set
 */
export function yunoTextResources(texts: CheckoutTexts): Record<string, string> {
  const resources: Record<string, string> = {}
  for (const [field, keys] of Object.entries(YUNO_TEXT_KEYS) as [
    CheckoutTextField,
    readonly string[],
  ][]) {
    const text = texts[field]
    if (!text) continue
    const value = YUNO_HTML_TEXTS.has(field) ? escapeHtml(text) : text
    for (const key of keys) resources[key] = value
  }
  return resources
}

/** The input style fields as CSS values; an unset field is `undefined`. */
export interface CheckoutInputStyle {
  bgColor?: string
  bgColorHover?: string
  bgColorActive?: string
  bgColorError?: string
  textColor?: string
  textSize?: string
  placeholderColor?: string
  labelColor?: string
  labelSize?: string
  errorMessageColor?: string
  borderColor?: string
  borderColorActive?: string
  borderColorError?: string
  borderWidth?: string
  borderRadius?: string
  paddingY?: string
  paddingX?: string
}

/** The wallet-button style fields as CSS values; an unset field is `undefined`. */
export interface CheckoutWalletStyle {
  marginTop?: string
  marginBottom?: string
  gap?: string
}

type Declaration = [property: string, value: string | undefined]

function rule(selectors: string[], declarations: Declaration[]): string {
  const body = declarations
    .filter((d): d is [string, string] => !!d[1])
    .map(([property, value]) => `${property}: ${value} !important;`)
    .join(' ')
  return body ? `${selectors.join(', ')} { ${body} }` : ''
}

const joinRules = (rules: string[]) => rules.filter(Boolean).join('\n')

/**
 * @param style the input style fields; only the set ones emit a declaration
 * @param scope prefix for every selector: `''` inside Yuno's card iframes, the
 *   container selector plus a space on the page
 * @returns CSS for Yuno's card fields, `''` when nothing is set
 */
export function yunoInputCss(style: CheckoutInputStyle, scope = ''): string {
  const s = style
  const sel = (...selectors: string[]) => selectors.map((x) => scope + x)
  const anyBackground = s.bgColor || s.bgColorHover || s.bgColorActive || s.bgColorError
  return joinRules([
    rule(sel('.sdk-payments-fieldset'), [
      ['padding-top', s.paddingY],
      ['padding-bottom', s.paddingY],
      ['padding-left', s.paddingX],
      ['padding-right', s.paddingX],
    ]),
    rule(sel('.sdk-payments-fieldset__box'), [
      ['background-color', s.bgColor],
      ['border-color', s.borderColor],
      ['border-width', s.borderWidth],
      ['border-radius', s.borderRadius],
    ]),
    rule(sel('.sdk-payments-fieldset__box:hover'), [['background-color', s.bgColorHover]]),
    rule(sel('.sdk-payments-fieldset__box.sdk-payments-fieldset__box--focus'), [
      ['background-color', s.bgColorActive],
      ['border-color', s.borderColorActive],
    ]),
    rule(sel('.sdk-payments-fieldset__box.sdk-payments-fieldset__box--error'), [
      ['background-color', s.bgColorError],
      ['border-color', s.borderColorError],
    ]),
    rule(sel('.sdk-payments-input__base'), [
      ['color', s.textColor],
      ['font-size', s.textSize],
      ['background-color', anyBackground ? 'transparent' : undefined],
    ]),
    rule(sel('.sdk-payments-input__base::placeholder'), [['color', s.placeholderColor]]),
    rule(sel('.sdk-payments-fieldset__label', '.sdk-payments-input__label'), [
      ['color', s.labelColor],
    ]),
    rule(
      sel(
        '.sdk-payments-fieldset__label',
        '.sdk-payments-input__label:not(.sdk-payments-input__label--focus):not(.sdk-payments-input__label--loaded)',
      ),
      [['font-size', s.labelSize]],
    ),
    rule(sel('.sdk-payments-error-text-field__message'), [['color', s.errorMessageColor]]),
  ])
}

/**
 * @param style the wallet-button style fields; only the set ones emit a declaration
 * @param scope the container selector plus a space
 * @returns CSS for Yuno's express buttons, `''` when nothing is set
 */
export function yunoWalletCss(style: CheckoutWalletStyle, scope: string): string {
  return joinRules([
    rule(
      [`${scope}.sdk-payments-external-button__container`],
      [
        ['gap', style.gap],
        ['margin-top', style.marginTop],
        ['margin-bottom', style.marginBottom],
      ],
    ),
    rule(
      [`${scope}.sdk-payments-minimal-card-form`],
      [['padding-top', style.marginBottom ? '0' : undefined]],
    ),
  ])
}

/**
 * @param value a CSS value, possibly `var(--x)`
 * @returns the variable's computed value on the page, for CSS bound for a
 *   cross-origin iframe; any other value unchanged
 */
export function resolveCssVar(value: string | undefined): string | undefined {
  if (!value || typeof window === 'undefined') return value
  const match = value.match(/^var\(--([\w-]+)\)/)
  if (!match) return value
  return (
    getComputedStyle(document.documentElement).getPropertyValue(`--${match[1]}`).trim() || value
  )
}
